import type { SortDirection } from "@nasam/kernel";

/** A new driver starts as PENDING_ACTIVATION (an activation email goes out)
 *  and becomes ACTIVE once they activate on the mobile app. */
export type DriverStatus = "PENDING_ACTIVATION" | "ACTIVE" | "INACTIVE";

/** A row in the drivers list. */
export interface Driver {
  readonly id: string;
  readonly nameEn: string;
  readonly nameAr: string;
  readonly avatarUrl: string | null;
  readonly nationalId: string;
  readonly licenseNumber: string;
  /** Calendar date, `YYYY-MM-DD`. */
  readonly licenseExpiryDate: string;
  readonly status: DriverStatus;
  /** Only feeds the assigned/not-assigned stat cards for now. */
  readonly isAssigned: boolean;
  /** Shown to Level 1 (staff) only; null for drivers under an individual
   *  subscription. */
  readonly company: NamedRef | null;
}

export interface NamedRef {
  readonly id: string;
  readonly nameEn: string;
  readonly nameAr: string;
}

/** Everything the read-only detail screen shows. Drivers sign in on the
 *  mobile app only, so there are no web credentials here. */
export interface DriverDetails extends Driver {
  readonly username: string;
  readonly email: string;
  readonly mobileNumber1: string;
  readonly mobileNumber2: string | null;
  readonly rfid: string | null;
  /** Calendar date, `YYYY-MM-DD`; optional on create. */
  readonly licenseIssueDate: string | null;
  readonly branch: NamedRef | null;
  /** Uploaded documents -- `GET /drivers/:id` returns them with the driver,
   *  so one request feeds both the details and the attachments tab. */
  readonly attachments: readonly DriverAttachment[];
}

export interface DriverAttachment {
  readonly id: string;
  readonly fileName: string;
  readonly mimeType: string;
  /** Unknown for files from the backend, which keeps no size. */
  readonly sizeBytes: number | null;
  readonly uploadedAt: string;
  readonly url: string;
}

/** A row in the Archived Drivers list -- what `restore` acts on. */
export interface ArchivedDriver {
  readonly id: string;
  readonly nameEn: string;
  readonly nameAr: string;
  /** The archived list carries no picture today; kept for when it does. */
  readonly avatarUrl: string | null;
  readonly username: string;
  readonly email: string;
  readonly company: NamedRef | null;
  readonly archivedAt: string;
}

export interface ArchivedDriversListQuery {
  readonly page?: number;
  readonly limit?: number;
  /** Direction on the archive date, the only sortable field. */
  readonly sort?: SortDirection;
  /** Matches the code, names, username and email. */
  readonly search?: string;
  /** ISO bounds on the archive date; set together. */
  readonly fromArchivedAt?: string;
  readonly toArchivedAt?: string;
}

/** The four stat cards -- subscription-wide, unaffected by search/filters. */
export interface DriversStats {
  readonly total: number;
  readonly assigned: number;
  readonly unassigned: number;
  readonly expiredLicenses: number;
}

/** The table's sortable columns, all on the backend's `sortBy` list (which
 *  has no national ID -- sending it fails the request). */
export type DriverSortBy =
  "nameEn" | "nameAr" | "licenseNumber" | "licenseExpiryDate" | "status";

export interface DriversListQuery {
  readonly page?: number;
  readonly limit?: number;
  readonly sort?: SortDirection;
  readonly sortBy?: DriverSortBy;
  /** Matches code, names, username, email, national ID, license number,
   *  phones and the company name. */
  readonly search?: string;
  readonly status?: DriverStatus;
  /** Inclusive calendar dates (`YYYY-MM-DD`) on the license expiry date, the
   *  backend's own parameter names; set together. */
  readonly fromLicenseExpiryDate?: string;
  readonly toLicenseExpiryDate?: string;
}

/**
 * Create Driver. The backend creates the driver's system account as
 * PENDING_ACTIVATION and sends the activation email.
 */
export interface DriverCreateInput {
  /** Level 1 (staff) only; a Level 2 caller's company is implied by their own
   *  account, so it stays null. The label rides along for display only. */
  readonly company: NamedRef | null;
  readonly branch: NamedRef;
  readonly nameAr: string;
  readonly nameEn: string;
  readonly nationalId: string;
  readonly username: string;
  readonly email: string;
  /** `+<country code><national number>`. */
  readonly mobileNumber1: string;
  readonly mobileNumber2?: string;
  readonly rfid?: string;
  readonly licenseNumber: string;
  /** Calendar dates, `YYYY-MM-DD`. */
  readonly licenseIssueDate: string;
  readonly licenseExpiryDate: string;
  readonly profilePicture?: File;
  readonly documents?: readonly File[];
}

/** Unique across all companies (case-insensitive for username/email). */
export type DriverUniqueField = "username" | "email" | "nationalId";

/** The archived driver whose username or email the new one collides with --
 *  offered for restore instead of a duplicate (409 ARCHIVED_DRIVER_FOUND). */
export interface ArchivedDriverMatch {
  readonly id: string;
  readonly nameEn: string;
  readonly nameAr: string;
  readonly archivedAt: string | null;
}

export type DriverCreateResult =
  | { readonly status: "created"; readonly driver: Driver }
  | {
      readonly status: "duplicate";
      /** The backend's localized 409 text, which names the taken value. */
      readonly message?: string;
      /** Only known to the mock: the backend's 409 names no field yet. */
      readonly field?: DriverUniqueField;
    }
  | {
      readonly status: "archived-match";
      readonly match: ArchivedDriverMatch;
      /** The backend's localized restore question, when it sends one. */
      readonly message?: string;
    };

/**
 * Update Driver: only the fields that changed (the PATCH takes a partial).
 * Username, email and company are fixed after creation; national ID /
 * passport and license number can change (unique, like on create); mobile 2 and RFID are sent as null to clear them. Documents are
 * only ever added here, never removed.
 */
export interface DriverUpdateInput {
  readonly branch?: NamedRef;
  readonly nameAr?: string;
  readonly nameEn?: string;
  readonly mobileNumber1?: string;
  readonly mobileNumber2?: string | null;
  readonly rfid?: string | null;
  readonly licenseIssueDate?: string;
  readonly licenseExpiryDate?: string;
  readonly nationalId?: string;
  readonly licenseNumber?: string;
  /** Never sent while the driver is pending activation (not admin-settable). */
  readonly status?: "ACTIVE" | "INACTIVE";
  readonly profilePicture?: File;
  readonly documents?: readonly File[];
}

export type DriverUpdateResult =
  | { readonly status: "updated"; readonly driver: DriverDetails }
  | {
      readonly status: "duplicate";
      /** The backend's localized 409 text, which names the taken value. */
      readonly message?: string;
    };

/** A license is expired once its expiry date is before `today` (date-only). */
export function isLicenseExpired(
  licenseExpiryDate: string,
  today: Date = new Date(),
): boolean {
  const [year, month, day] = licenseExpiryDate.split("-").map(Number);
  if (year === undefined || month === undefined || day === undefined) {
    return false;
  }
  const expiry = new Date(year, month - 1, day);
  const startOfToday = new Date(
    today.getFullYear(),
    today.getMonth(),
    today.getDate(),
  );
  return expiry < startOfToday;
}
